import { startTransition, useEffect, useMemo, useState } from "react"

import { FleetB2BPageShell } from "@/features/operator/fleet/fleet-b2b-page-shell"
import {
  canAccessFleetSuperAdminControls,
  titleizeToken,
} from "@/features/operator/shared/operator-access"
import { useOptionalOperatorShellSession } from "@/features/operator/shared/operator-shell-session-context"
import {
  OperatorEmptyState,
  OperatorMetricCard,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import {
  apiClient,
  isApiError,
  type FleetAuthorityDirectoryRecord,
} from "@/lib/api/client"

const COVERAGE_PRIORITY: Record<string, number> = {
  strong: 0,
  partial: 1,
  fragmented: 2,
}

const AUTHORITY_SECTION_ORDER = [
  "integrated_emergency",
  "police",
  "fire",
  "ambulance",
  "disaster_control",
  "supplemental_control_room",
] as const

interface AuthorityDirectoryStateGroup {
  capturedAt?: string | null
  country?: string | null
  coverageStatus?: string | null
  digitalRows: FleetAuthorityDirectoryRecord[]
  entryModel?: string | null
  fallbackRows: FleetAuthorityDirectoryRecord[]
  id: string
  primaryRows: FleetAuthorityDirectoryRecord[]
  regionGroup?: string | null
  rows: FleetAuthorityDirectoryRecord[]
  serviceSections: Array<{
    authorityType: string
    rows: FleetAuthorityDirectoryRecord[]
  }>
  stateOrUt: string
  totalRows: number
  verificationStatus?: string | null
}

function formatDate(value?: string | null) {
  if (!value) {
    return "Not recorded"
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
}

function formatCoverageLabel(value?: string | null) {
  return value ? titleizeToken(value) : "Unknown"
}

function formatChannelLabel(value?: string | null) {
  if (!value) {
    return "Unknown"
  }

  if (value === "whatsapp") {
    return "WhatsApp"
  }

  return titleizeToken(value)
}

function formatAuthorityLabel(value?: string | null) {
  return value ? titleizeToken(value) : "Unknown"
}

function buildRowKey(row: FleetAuthorityDirectoryRecord) {
  return [
    row.id,
    row.stateOrUt,
    row.authorityType,
    row.channelType,
    row.channelValue,
  ].join(":")
}

function compareRows(left: FleetAuthorityDirectoryRecord, right: FleetAuthorityDirectoryRecord) {
  const leftPriority = left.routingPriority ?? 999
  const rightPriority = right.routingPriority ?? 999

  if (leftPriority !== rightPriority) {
    return leftPriority - rightPriority
  }

  return `${left.authorityType || ""}:${left.channelType || ""}:${left.channelValue || ""}`
    .localeCompare(`${right.authorityType || ""}:${right.channelType || ""}:${right.channelValue || ""}`)
}

function normalizeSourceUrls(rows: FleetAuthorityDirectoryRecord[]) {
  const seen = new Set<string>()
  const urls: string[] = []

  rows.forEach((row) => {
    ;(row.sourceUrls || []).forEach((sourceUrl) => {
      if (!sourceUrl || seen.has(sourceUrl)) {
        return
      }

      seen.add(sourceUrl)
      urls.push(sourceUrl)
    })
  })

  return urls
}

function matchesStateSearch(group: AuthorityDirectoryStateGroup, query: string) {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) {
    return true
  }

  return [
    group.stateOrUt,
    group.country,
    group.regionGroup,
    group.coverageStatus,
    group.entryModel,
    group.verificationStatus,
    ...group.rows.flatMap((row) => [
      row.authorityType,
      row.channelType,
      row.channelValue,
      row.notes,
      ...(row.sourceUrls || []),
    ]),
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(normalizedQuery))
}

function buildStateGroups(entries: FleetAuthorityDirectoryRecord[]) {
  const groups = new Map<string, AuthorityDirectoryStateGroup>()

  for (const entry of entries) {
    const stateKey = entry.stateOrUt || entry.id
    const existing = groups.get(stateKey)

    if (!existing) {
      groups.set(stateKey, {
        capturedAt: entry.capturedAt || null,
        country: entry.country || null,
        coverageStatus: entry.coverageStatus || null,
        digitalRows: [],
        entryModel: entry.entryModel || null,
        fallbackRows: [],
        id: stateKey,
        primaryRows: [],
        regionGroup: entry.regionGroup || null,
        rows: [entry],
        serviceSections: [],
        stateOrUt: entry.stateOrUt || "Unknown state",
        totalRows: 1,
        verificationStatus: entry.verificationStatus || null,
      })
      continue
    }

    existing.rows.push(entry)
    existing.totalRows += 1
  }

  const orderedGroups = [...groups.values()].map((group) => {
    const rows = [...group.rows].sort(compareRows)

    const serviceSections = AUTHORITY_SECTION_ORDER.map((authorityType) => ({
      authorityType,
      rows: rows.filter((row) => row.authorityType === authorityType),
    })).filter((section) => section.rows.length > 0)

    return {
      ...group,
      digitalRows: rows.filter((row) => row.channelType === "whatsapp" || row.channelType === "email"),
      fallbackRows: rows.filter((row) => row.isFallback),
      primaryRows: rows.filter((row) => row.isPrimaryForV1),
      rows,
      serviceSections,
    }
  })

  return orderedGroups.sort((left, right) => {
    const leftPriority = COVERAGE_PRIORITY[left.coverageStatus || ""] ?? 99
    const rightPriority = COVERAGE_PRIORITY[right.coverageStatus || ""] ?? 99

    if (leftPriority !== rightPriority) {
      return leftPriority - rightPriority
    }

    return left.stateOrUt.localeCompare(right.stateOrUt)
  })
}

function DirectoryRowStack({
  emptyMessage,
  rows,
}: {
  emptyMessage: string
  rows: FleetAuthorityDirectoryRecord[]
}) {
  if (!rows.length) {
    return (
      <div className="rounded-2xl border border-dashed border-border/70 bg-background/50 px-4 py-4 text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    )
  }

  return (
    <div className="grid gap-3">
      {rows.map((row) => (
        <div
          key={buildRowKey(row)}
          className="rounded-[1.6rem] border border-border/70 bg-background/75 px-4 py-4 shadow-xs"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                {formatAuthorityLabel(row.authorityType)} · {formatChannelLabel(row.channelType)}
              </p>
              <div className="mt-2 text-lg font-semibold text-foreground">
                {row.channelValue || "No channel value recorded"}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              {row.isPrimaryForV1 ? (
                <span className="rounded-full bg-primary/10 px-3 py-1 font-medium text-primary">
                  Primary
                </span>
              ) : null}
              {row.isFallback ? (
                <span className="rounded-full bg-secondary px-3 py-1 font-medium text-secondary-foreground">
                  Fallback
                </span>
              ) : null}
              {typeof row.routingPriority === "number" ? (
                <span className="rounded-full border border-border/70 px-3 py-1 font-medium text-muted-foreground">
                  Priority {row.routingPriority}
                </span>
              ) : null}
            </div>
          </div>

          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            {row.notes || "No operator guidance note is recorded for this channel."}
          </p>
        </div>
      ))}
    </div>
  )
}

export function FleetAuthorityDirectoryPage() {
  const shellSession = useOptionalOperatorShellSession()
  const [entries, setEntries] = useState<FleetAuthorityDirectoryRecord[]>([])
  const [selectedStateId, setSelectedStateId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const isFleetSafetySuperAdmin = useMemo(() => {
    return shellSession?.status === "ready"
      && canAccessFleetSuperAdminControls(shellSession.session)
  }, [shellSession])

  useEffect(() => {
    if (!isFleetSafetySuperAdmin) {
      return
    }

    let isActive = true

    async function loadDirectory() {
      startTransition(() => {
        setIsLoading(true)
        setError(null)
      })

      try {
        const nextEntries = await apiClient.getFleetAuthorityDirectory()
        if (!isActive) {
          return
        }

        const sortedEntries = [...nextEntries].sort(compareRows)
        const nextGroups = buildStateGroups(sortedEntries)

        startTransition(() => {
          setEntries(sortedEntries)
          setSelectedStateId((current) => {
            if (current && nextGroups.some((group) => group.id === current)) {
              return current
            }

            return nextGroups[0]?.id || null
          })
        })
      } catch (loadError) {
        if (!isActive) {
          return
        }

        startTransition(() => {
          setEntries([])
          setSelectedStateId(null)
          setError(
            isApiError(loadError)
              ? loadError.message
              : loadError instanceof Error
                ? loadError.message
                : "Unable to load the operational public authority directory."
          )
        })
      } finally {
        if (isActive) {
          startTransition(() => {
            setIsLoading(false)
          })
        }
      }
    }

    void loadDirectory()

    return () => {
      isActive = false
    }
  }, [isFleetSafetySuperAdmin])

  const stateGroups = useMemo(() => buildStateGroups(entries), [entries])

  const filteredStateGroups = useMemo(() => {
    return stateGroups.filter((group) => matchesStateSearch(group, searchQuery))
  }, [searchQuery, stateGroups])

  const selectedState = useMemo(() => {
    return filteredStateGroups.find((group) => group.id === selectedStateId)
      || stateGroups.find((group) => group.id === selectedStateId)
      || null
  }, [filteredStateGroups, selectedStateId, stateGroups])

  const directoryMetrics = useMemo(() => {
    return {
      digitalChannels: entries.filter((entry) => entry.channelType === "whatsapp" || entry.channelType === "email").length,
      fragmentedStates: stateGroups.filter((group) => group.coverageStatus === "fragmented").length,
      operationalRows: entries.length,
      primaryRecommendations: entries.filter((entry) => entry.isPrimaryForV1).length,
      totalStates: stateGroups.length,
    }
  }, [entries, stateGroups])

  const selectedStateSourceUrls = useMemo(() => {
    return selectedState ? normalizeSourceUrls(selectedState.rows) : []
  }, [selectedState])

  if (!isFleetSafetySuperAdmin) {
    return (
      <FleetB2BPageShell
        activeSection="overview"
        title="Public authority directory"
        description="Operational public authority routing access is reserved for Fleet Safety super admins."
        highlights={[
          "Review the statewide operational directory derived from the authority CSV.",
          "Compare integrated, police, fire, ambulance, digital, and fallback channels in one place.",
          "Keep this directory separate from the live authority incident desk.",
        ]}
      >
        <OperatorEmptyState
          title="Public authority directory is not available for this session."
          message="A Fleet Safety super-admin session is required to view this directory."
        />
      </FleetB2BPageShell>
    )
  }

  return (
    <FleetB2BPageShell
      activeSection="overview"
      title="Public authority directory"
      description="Review the full operational statewide authority directory derived from the June 2, 2026 CSV without leaving the Fleet super-admin lane."
      highlights={[
        "This surface now exposes the full 263-row operational directory, not the compressed 33-row fallback view.",
        "Every state groups integrated emergency, police, fire, ambulance, disaster-control, supplemental, WhatsApp, and email channels separately.",
        "The dataset remains statewide and public-source-derived; district-by-district routing is still outside this page.",
      ]}
    >
      <div className="grid gap-5">
        <OperatorPanel
          title="Operational posture"
          description="These metrics summarize the full operational directory currently available to Fleet super-admin sessions."
        >
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <OperatorMetricCard
              label="States and UTs"
              value={directoryMetrics.totalStates}
              detail="Distinct state-level directory groups."
            />
            <OperatorMetricCard
              label="Operational rows"
              value={directoryMetrics.operationalRows}
              detail="Full operational channels loaded from the CSV-derived table."
            />
            <OperatorMetricCard
              label="Primary recommendations"
              value={directoryMetrics.primaryRecommendations}
              detail="Rows marked primary for V1 routing."
            />
            <OperatorMetricCard
              label="Digital channels"
              value={directoryMetrics.digitalChannels}
              detail={`${directoryMetrics.fragmentedStates} states are still marked fragmented.`}
            />
          </div>
        </OperatorPanel>

        {error ? (
          <OperatorStatusMessage tone="danger">{error}</OperatorStatusMessage>
        ) : null}

        <OperatorStatusMessage>
          This page shows the operational statewide public directory seeded from the CSV and grouped
          into direct-use channel sections. It is intended for Fleet super-admin routing reference,
          not for live authority incident handling.
        </OperatorStatusMessage>

        <div className="grid gap-5 xl:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)]">
          <OperatorPanel
            title="States and UTs"
            description="Search by state, authority type, channel, or note to locate the operational routing profile you need."
          >
            <div className="grid gap-4">
              <label className="grid gap-2 text-sm">
                <span className="font-medium text-muted-foreground">Search operational directory</span>
                <input
                  aria-label="Search public authority directory states"
                  className="rounded-2xl border border-border bg-background px-3 py-2"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search by state, authority type, channel, or number"
                />
              </label>

              {isLoading ? (
                <OperatorStatusMessage>Loading operational public authority directory...</OperatorStatusMessage>
              ) : null}

              {!isLoading && !filteredStateGroups.length ? (
                <OperatorEmptyState
                  title="No states match the current filter."
                  message="Try a broader state, authority type, or channel search."
                />
              ) : null}

              {!isLoading && filteredStateGroups.length ? (
                <div className="grid gap-3">
                  {filteredStateGroups.map((group) => {
                    const isSelected = group.id === selectedStateId

                    return (
                      <button
                        key={group.id}
                        type="button"
                        className={
                          isSelected
                            ? "rounded-[1.8rem] border border-primary/35 bg-primary/5 px-4 py-4 text-left"
                            : "rounded-[1.8rem] border border-border/70 bg-background/65 px-4 py-4 text-left transition-colors hover:border-primary/25 hover:bg-background/80"
                        }
                        onClick={() => setSelectedStateId(group.id)}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-base font-semibold">{group.stateOrUt}</p>
                            <p className="mt-1 text-sm text-muted-foreground">
                              {[group.regionGroup, group.country].filter(Boolean).join(" · ") || "Region not recorded"}
                            </p>
                          </div>
                          <div className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                            {formatCoverageLabel(group.coverageStatus)}
                          </div>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
                          <span>{group.totalRows} operational rows</span>
                          <span>{group.primaryRows.length} primary</span>
                          <span>{group.digitalRows.length} digital</span>
                          <span>{group.fallbackRows.length} fallback</span>
                        </div>
                      </button>
                    )
                  })}
                </div>
              ) : null}
            </div>
          </OperatorPanel>

          <OperatorPanel
            title="State detail"
            description={
              selectedState
                ? "Review operational routing rows grouped by service and channel."
                : "Select a state or UT from the left to inspect the operational directory."
            }
          >
            {!isLoading && !selectedState ? (
              <OperatorEmptyState
                title="No state selected."
                message="Choose a state or UT from the left to inspect its operational routing rows."
              />
            ) : null}

            {!isLoading && selectedState ? (
              <div className="grid gap-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                      {[selectedState.country, selectedState.regionGroup].filter(Boolean).join(" · ") || "India"}
                    </p>
                    <h3 className="mt-2 text-2xl font-semibold">{selectedState.stateOrUt}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Entry model {formatCoverageLabel(selectedState.entryModel)} · Verification {formatCoverageLabel(selectedState.verificationStatus)}
                    </p>
                  </div>
                  <div className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                    {formatCoverageLabel(selectedState.coverageStatus)}
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                    <div className="text-sm text-muted-foreground">Operational rows</div>
                    <div className="mt-3 text-2xl font-semibold">{selectedState.totalRows}</div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      Captured {formatDate(selectedState.capturedAt)}
                    </div>
                  </div>
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                    <div className="text-sm text-muted-foreground">Primary rows</div>
                    <div className="mt-3 text-2xl font-semibold">{selectedState.primaryRows.length}</div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      Rows explicitly marked primary for V1 routing.
                    </div>
                  </div>
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                    <div className="text-sm text-muted-foreground">Digital rows</div>
                    <div className="mt-3 text-2xl font-semibold">{selectedState.digitalRows.length}</div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      WhatsApp and email escalation channels.
                    </div>
                  </div>
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                    <div className="text-sm text-muted-foreground">Fallback rows</div>
                    <div className="mt-3 text-2xl font-semibold">{selectedState.fallbackRows.length}</div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      Backup operational channels and control-room contacts.
                    </div>
                  </div>
                </div>

                <div className="grid gap-4">
                  {selectedState.serviceSections.map((section) => (
                    <div
                      key={section.authorityType}
                      className="rounded-[1.8rem] border border-border/70 bg-background/60 px-4 py-4"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <h4 className="text-lg font-semibold">
                            {formatAuthorityLabel(section.authorityType)}
                          </h4>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {section.rows.length} operational row{section.rows.length === 1 ? "" : "s"} in this service group.
                          </p>
                        </div>
                      </div>
                      <div className="mt-4">
                        <DirectoryRowStack
                          emptyMessage="No channels recorded in this service section."
                          rows={section.rows}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <div className="rounded-[1.8rem] border border-border/70 bg-background/60 px-4 py-4">
                    <h4 className="text-lg font-semibold">WhatsApp and email</h4>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Digital escalation channels published for this state.
                    </p>
                    <div className="mt-4">
                      <DirectoryRowStack
                        emptyMessage="No public WhatsApp or email channels are recorded for this state."
                        rows={selectedState.digitalRows}
                      />
                    </div>
                  </div>

                  <div className="rounded-[1.8rem] border border-border/70 bg-background/60 px-4 py-4">
                    <h4 className="text-lg font-semibold">Fallback and control rooms</h4>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Backup and supplemental rows that operators may need when primary channels fail.
                    </p>
                    <div className="mt-4">
                      <DirectoryRowStack
                        emptyMessage="No fallback or control-room rows are recorded for this state."
                        rows={selectedState.fallbackRows.filter((row) => !selectedState.digitalRows.includes(row))}
                      />
                    </div>
                  </div>
                </div>

                <div className="rounded-[1.8rem] border border-border/70 bg-background/60 px-4 py-4">
                  <h4 className="text-lg font-semibold">Source URLs</h4>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Unique public sources attached to the operational rows for this state.
                  </p>
                  <div className="mt-4 grid gap-3">
                    {selectedStateSourceUrls.length ? (
                      selectedStateSourceUrls.map((sourceUrl) => (
                        <a
                          key={sourceUrl}
                          href={sourceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-2xl border border-border/70 bg-background/80 px-4 py-4 text-sm text-primary underline-offset-4 hover:underline"
                        >
                          {sourceUrl}
                        </a>
                      ))
                    ) : (
                      <OperatorEmptyState
                        title="No source URLs recorded."
                        message="This state currently has no source URLs attached to its operational rows."
                      />
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </OperatorPanel>
        </div>
      </div>
    </FleetB2BPageShell>
  )
}
